// @givmo/board — a director's move of their own seat to another Givmo account, as the pages keep it
// (CBG-SEC2-L4-MOVE; D-CBG-23 flow (b), Jonathan, 2026-10-07 19:14 PDT: "i agree with your recs to the
// four questions btw").
//
// The move has two proofs and spans two accounts in one tab:
//
//   1. signed in to the account the seat is on, the director starts it: the backend answers a
//      single-use TICKET, live 30 minutes, bound to the seat and to that account;
//   2. that account is signed out on this browser, the director signs in to (or creates) the other
//      account, which proves the seat's address — its own confirmed email is that address, or a code
//      mailed there — and finishes it.
//
// THE TICKET LIVES IN THIS TAB ONLY, never in an address or a mail (the join marker's rule,
// `join-marker.ts`): the step that needs it runs after a sign-out, a create form and an email
// confirmation in this same tab. `sessionStorage` holds it with the few facts the signed-out pages say
// (the board's name, the seat's address masked). Every access is guarded: with storage refused, the
// page that comes back signed out has no ticket and says the move ended, and nothing moved.

/** What this tab keeps between the start and the finish. */
export interface StoredSeatMove {
  /** The ticket's plaintext, as the start answered it once. */
  token: string;
  /** When it stops working (an ISO instant). */
  expiresAt: string;
  /** The board the seat is on, by the address every director page carries. */
  orgRef: string;
  organizationName: string | null;
  /** The seat's address, masked, for the create form's line. */
  maskedEmailOfRecord: string;
  /** Which way the director chose: a new account (frame b2), or one they have (b2e). */
  via: SeatMoveVia;
  /**
   * The seat being moved (`board_member_id`) and the account it started from (the account's own
   * `uid`), both never shown. Once the move has finished, the backend refuses the spent ticket like
   * any ended one; these let the page tell the two apart (the seat on another account's `/me` is a
   * finished move), so a reload after the move never says nothing moved. Null when not known.
   */
  seatId?: string | null;
  startedBy?: string | null;
}

/** "Create new account" (b2) or "Move to another Givmo account" (b2e). */
export type SeatMoveVia = 'new' | 'existing';

const KEY = 'board-director-seat-move';
const CREATE_KEY = 'board-director-seat-move-create';

/** `via` from a search, defaulting to the leading button's way, a new account. */
export function seatMoveViaOf(value: unknown): SeatMoveVia {
  return value === 'existing' ? 'existing' : 'new';
}

/** Keep the ticket for this tab's next steps. */
export function rememberSeatMove(move: StoredSeatMove): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(move));
  } catch {
    // Storage refused: the signed-out page has no ticket, and says the move ended.
  }
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/**
 * The ticket this tab holds, or null when there is none, it is malformed, or its 30 minutes are up
 * (the backend refuses it then too: the page shows the move ended either way). Read, not consumed.
 */
export function seatMoveInTab(now: number = Date.now()): StoredSeatMove | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const row = parsed as Record<string, unknown>;
    const token = text(row.token);
    const expiresAt = text(row.expiresAt);
    const orgRef = text(row.orgRef);
    const maskedEmailOfRecord = text(row.maskedEmailOfRecord);
    if (token === null || expiresAt === null || orgRef === null || maskedEmailOfRecord === null)
      return null;
    const expires = Date.parse(expiresAt);
    if (Number.isNaN(expires) || expires <= now) return null;
    return {
      token,
      expiresAt,
      orgRef,
      organizationName: text(row.organizationName),
      maskedEmailOfRecord,
      via: seatMoveViaOf(row.via),
      seatId: text(row.seatId),
      startedBy: text(row.startedBy),
    };
  } catch {
    return null;
  }
}

/** Forget the ticket: the move finished, or ended. */
export function forgetSeatMove(): void {
  try {
    window.sessionStorage.removeItem(KEY);
    window.sessionStorage.removeItem(CREATE_KEY);
  } catch {
    // Nothing to forget where nothing could be stored.
  }
}

/**
 * "Create new account" from a signed-in page of the move (b9a, b9b, the start's new way): the page
 * signs out back to itself, and the page the console remounts signed out opens the create form. The
 * invitation's own one-shot marker (`markCreateAnotherAccount`) is this shape, and for its reason:
 * opening the form straight after the sign-out would race the form's guard.
 */
export function markCreateForSeatMove(): void {
  try {
    window.sessionStorage.setItem(CREATE_KEY, '1');
  } catch {
    // Storage refused: the signed-out page shows the ways in, with "Create new account" under them.
  }
}

/** Whether this tab asked for the create form; consumed either way. */
export function takeCreateForSeatMove(): boolean {
  try {
    const marked = window.sessionStorage.getItem(CREATE_KEY);
    window.sessionStorage.removeItem(CREATE_KEY);
    return marked !== null;
  } catch {
    return false;
  }
}

const MASK = '•••';

/**
 * An address as a Board page may show it before the backend has: the first character of the mailbox,
 * three bullets, then the whole domain — `d•••@example.org`. The backend's own rule, byte for byte
 * (`mask_email_address`); the start page (frames b2, b2e) says where the code will go before it asks
 * for anything, from the seat's own address on the director's directory entry.
 */
export function maskEmailAddress(email: string | null | undefined): string {
  const address = (email ?? '').trim().toLowerCase();
  const at = address.lastIndexOf('@');
  const local = at < 0 ? '' : address.slice(0, at);
  const domain = at < 0 ? '' : address.slice(at + 1);
  if (local.length === 0 || domain.length === 0) return MASK;
  // The first CHARACTER, as the backend's `local[0]` takes it, never half of a surrogate pair.
  return `${[...local][0]}${MASK}@${domain}`;
}

/** The move's start, as the page decodes it: the ticket and the seat's address, masked. */
export interface SeatMoveTicket {
  token: string;
  expiresAt: string;
  maskedEmailOfRecord: string;
}

/** The start's answer (`BoardSeatMoveTicketOut`), read tolerantly: a malformed one is none. */
export function decodeSeatMoveTicket(data: unknown): SeatMoveTicket | null {
  const row = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null;
  const token = text(row?.move_token);
  const expiresAt = text(row?.expires_at);
  const masked = text(row?.masked_email_of_record);
  if (token === null || expiresAt === null || masked === null) return null;
  return { token, expiresAt, maskedEmailOfRecord: masked };
}

/** What a ticket is for, before the move (`BoardSeatMovePreviewOut`). */
export interface SeatMovePreview {
  organizationName: string | null;
  seatDisplayName: string;
  maskedEmailOfRecord: string;
  /** The account the seat is on, masked; null when its address can't be read. */
  movingFromMasked: string | null;
  /** The signed-in account IS the one the seat is on (frame b9a). */
  fromThisAccount: boolean;
  /** The signed-in account's confirmed email is the seat's address: no code (frame b4m). */
  emailMatchesSeat: boolean;
  /** Another organization's sign-in lock binds the signed-in account too (frame b9b). */
  anotherAccountRequired: boolean | null;
}

/** The preview's answer, read tolerantly: a malformed one is none. */
export function decodeSeatMovePreview(data: unknown): SeatMovePreview | null {
  const row = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null;
  const seat = text(row?.seat_display_name);
  const masked = text(row?.masked_email_of_record);
  if (row === null || seat === null || masked === null) return null;
  return {
    organizationName: text(row.organization_name),
    seatDisplayName: seat,
    maskedEmailOfRecord: masked,
    movingFromMasked: text(row.moving_from_masked),
    fromThisAccount: row.from_this_account === true,
    emailMatchesSeat: row.email_matches_seat === true,
    anotherAccountRequired:
      typeof row.another_account_required === 'boolean' ? row.another_account_required : null,
  };
}
